import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { alpha, borderAlpha } from "../components/mobile/primitives";

const principles = [
  {
    title: "Single-Stroke Clarity",
    text: "One weight, one color per icon. No dual layers, no gradient halos. Reads at 12px and 48px alike.",
  },
  {
    title: "Rounded Caps, Open Counters",
    text: "Round strokeLinecap and strokeLinejoin everywhere. Generous negative space for calm readability.",
  },
  {
    title: "Color Carries Meaning",
    text: "The icon container tint tells the story — green for health, lavender for AI, gold for nutrition, orange for alerts.",
  },
  {
    title: "Works at Every Size",
    text: "Designed for mobile tab bars (20px) and dashboard cards (48px). No detail that breaks at small sizes.",
  },
];

/* ══════════════════════════════════════════════════════════════
   ICON DATA — single-path, no dual layer
   Color is on the container, not split across paths.
   ══════════════════════════════════════════════════════════════ */
const healthIcons = [
  {
    name: "Heart",
    category: "Vitals",
    color: "var(--color-kin-green-soft)",
    path: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z",
  },
  {
    name: "Lab Flask",
    category: "Labs",
    color: "var(--color-kin-gold-dark)",
    path: "M9 3v7l-4 8a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-4-8V3M8 3h8",
  },
  {
    name: "Sleep Moon",
    category: "Sleep",
    color: "var(--color-kin-lavender)",
    path: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z",
  },
  {
    name: "Activity Pulse",
    category: "Activity",
    color: "var(--color-kin-green-soft)",
    path: "M22 12h-4l-3 9L9 3l-3 9H2",
  },
  {
    name: "Shield",
    category: "Safety",
    color: "var(--color-kin-green-soft)",
    path: "M12 3l7.5 3v6c0 4.97-3.026 9.36-7.5 11-4.474-1.64-7.5-6.03-7.5-11V6L12 3z",
  },
  {
    name: "Alert",
    category: "Risk",
    color: "var(--color-kin-orange)",
    path: "M12 3.75l8.25 14.25a1.5 1.5 0 0 1-1.3 2.25H5.05a1.5 1.5 0 0 1-1.3-2.25L12 3.75zM12 9v4.25M12 16h.01",
  },
  {
    name: "Capsule",
    category: "Medication",
    color: "var(--color-kin-gold-dark)",
    path: "M8.25 7.5h7.5a3.75 3.75 0 0 1 0 7.5h-7.5a3.75 3.75 0 0 1 0-7.5zM12 7.5v7.5",
  },
  {
    name: "Thermometer",
    category: "Temperature",
    color: "var(--color-kin-orange)",
    path: "M14.5 4.5a2.5 2.5 0 0 0-5 0v9.26a3.5 3.5 0 1 0 5 0V4.5zM12 16a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM12 13V5.5",
  },
  {
    name: "Wearable",
    category: "Device",
    color: "var(--color-kin-lavender)",
    path: "M6 2h12v20H6zM10 8h4M10 12h4M10 16h2",
    viewBox: "4 0 16 24",
  },
  {
    name: "Hydration",
    category: "Water",
    color: "var(--color-kin-green-soft)",
    path: "M7.5 4.5h9l-1.5 13.5h-6L7.5 4.5zM9 9h6",
  },
  {
    name: "Nutrition",
    category: "Diet",
    color: "var(--color-kin-gold-dark)",
    path: "M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06zM12 5V2",
  },
  {
    name: "Barbell",
    category: "Strength",
    color: "var(--color-kin-green-soft)",
    path: "M3.75 9.75v4.5M20.25 9.75v4.5M6 8.25v7.5M18 8.25v7.5M8.25 10.5h7.5v3h-7.5z",
  },
];

const dataIcons = [
  {
    name: "Upload",
    category: "Import",
    color: "var(--color-kin-green-soft)",
    path: "M12 15V5M8.5 8.5 12 5l3.5 3.5M4 15.5v2A1.5 1.5 0 0 0 5.5 19h13a1.5 1.5 0 0 0 1.5-1.5v-2",
  },
  {
    name: "Document",
    category: "Files",
    color: "var(--color-kin-green-soft)",
    path: "M7 4h6l4 4v11a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM13 4v4h4M9 13h6M9 16h4",
  },
  {
    name: "Scan",
    category: "Camera",
    color: "var(--color-kin-gold-dark)",
    path: "M7 4H5.5A1.5 1.5 0 0 0 4 5.5V7M17 4h1.5A1.5 1.5 0 0 1 20 5.5V7M20 17v1.5a1.5 1.5 0 0 1-1.5 1.5H17M7 20H5.5A1.5 1.5 0 0 1 4 18.5V17M8 12h8",
  },
  {
    name: "Note",
    category: "Journal",
    color: "var(--color-kin-gold-dark)",
    path: "M7 4h10a1.5 1.5 0 0 1 1.5 1.5v13L15 16H7a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 7 4zM9 9h6M9 12h4",
  },
  {
    name: "Microphone",
    category: "Voice",
    color: "var(--color-kin-lavender)",
    path: "M9 4.5h6v11a3 3 0 0 1-6 0zM6.5 11.5v1a5.5 5.5 0 0 0 11 0v-1M12 18v2.5",
    viewBox: "4 2 16 22",
  },
  {
    name: "Camera",
    category: "Photo",
    color: "var(--color-kin-green-soft)",
    path: "M3.5 6.5h17v12a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2zM8.2 6.5 9.4 4.6h5.2l1.2 1.9",
    extra: "M12 12.5a3.3 3.3 0 1 0 0-.01",
  },
];

const aiIcons = [
  {
    name: "Spark",
    category: "AI Insight",
    color: "var(--color-kin-lavender)",
    path: "M9.81 15.9 9 18.75l-.81-2.85a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.85-.81a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.81 2.85a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.85.81a4.5 4.5 0 0 0-3.09 3.09zM18 4.5l.45 1.35 1.35.45-1.35.45-.45 1.35-.45-1.35-1.35-.45 1.35-.45z",
  },
  {
    name: "Clock",
    category: "Processing",
    color: "var(--color-kin-lavender)",
    path: "M12 6v6l4 2M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z",
  },
  {
    name: "Verified",
    category: "Trusted",
    color: "var(--color-kin-green-soft)",
    path: "M9 12.75 11.25 15 15 9.75M21 12c0 1.27-.63 2.39-1.59 3.07a3.75 3.75 0 0 1-1.04 3.3 3.75 3.75 0 0 1-3.3 1.04A3.75 3.75 0 0 1 12 21a3.75 3.75 0 0 1-3.07-1.59 3.75 3.75 0 0 1-3.3-1.04 3.75 3.75 0 0 1-1.04-3.3A3.75 3.75 0 0 1 3 12c0-1.27.63-2.39 1.59-3.07a3.75 3.75 0 0 1 1.04-3.3 3.75 3.75 0 0 1 3.3-1.04A3.75 3.75 0 0 1 12 3c1.27 0 2.39.63 3.07 1.59a3.75 3.75 0 0 1 3.3 1.04 3.75 3.75 0 0 1 1.04 3.3A3.75 3.75 0 0 1 21 12z",
  },
  {
    name: "Unverified",
    category: "Review",
    color: "var(--color-kin-gold-dark)",
    path: "M7 4h6l4 4v11a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM13 4v4h4M12 11v3M12 17h.01",
  },
  {
    name: "Chat",
    category: "Conversation",
    color: "var(--color-kin-lavender)",
    path: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  },
  {
    name: "Book",
    category: "Reading",
    color: "var(--color-kin-lavender)",
    path: "M12 6A9 9 0 0 0 6 3.75c-1.05 0-2.06.18-3 .51v14.25A9 9 0 0 1 6 18c2.3 0 4.41.87 6 2.29m0-14.25A9 9 0 0 1 18 3.75c1.05 0 2.06.18 3 .51v14.25A9 9 0 0 0 18 18a9 9 0 0 0-6 2.29m0-14.25v14.25",
  },
];

const platformIcons = [
  {
    name: "Lock",
    category: "Vault",
    color: "var(--color-kin-green-soft)",
    path: "M16.5 10.5V7.5a4.5 4.5 0 1 0-9 0v3M6 10.5h12a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5v-6A1.5 1.5 0 0 1 6 10.5zM12 14.25v2.25",
  },
  {
    name: "Bell",
    category: "Notifications",
    color: "var(--color-kin-orange)",
    path: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9zM13.73 21a2 2 0 0 1-3.46 0",
  },
  {
    name: "Search",
    category: "Find",
    color: "var(--color-kin-green-soft)",
    path: "M21 21l-5.2-5.2M15.8 9.9a5.9 5.9 0 1 1-11.8 0 5.9 5.9 0 0 1 11.8 0z",
  },
  {
    name: "Profile",
    category: "Account",
    color: "var(--color-kin-green-soft)",
    path: "M12 8.1a3.1 3.1 0 1 0 0-.01M5.3 19.5c1.7-3 4-4.5 6.7-4.5s5 1.5 6.7 4.5",
  },
  {
    name: "Trend",
    category: "Analytics",
    color: "var(--color-kin-green-soft)",
    path: "M3.75 3v16.5h16.5M6 16.5l4.5-6 3 3 4.5-7.5",
  },
  {
    name: "Gear",
    category: "Settings",
    color: "var(--color-kin-green-soft)",
    path: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  },
  {
    name: "Calendar",
    category: "Timeline",
    color: "var(--color-kin-gold-dark)",
    path: "M6.75 3v2.25M17.25 3v2.25M3 7.5h18M5.25 5.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25z",
  },
  {
    name: "Share",
    category: "Export",
    color: "var(--color-kin-lavender)",
    path: "M7.22 10.91a2.25 2.25 0 1 0 0 2.18M7.22 10.91l9.56-5.31M7.22 13.09l9.56 5.31M19 7a2.25 2.25 0 1 0-2.22-1.4M19 17a2.25 2.25 0 1 0-2.22 1.4",
  },
];

/* ── Flat list for export ─────────────────────────────────── */
const iconSet = [...healthIcons, ...dataIcons, ...aiIcons, ...platformIcons];
export {
  healthIcons,
  dataIcons as dataSourceIcons,
  aiIcons as aiActivityIcons,
  platformIcons,
  iconSet,
};

/* ── Pillar icons (used in Philosophy section) ───────────── */
export const pillarIcons = {
  own: {
    path: "M16.5 10.5V7.5a4.5 4.5 0 1 0-9 0v3M6 10.5h12a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5v-6A1.5 1.5 0 0 1 6 10.5zM12 14.25v2.25",
    detail: "",
    tone: "text-kin-green-dark",
  },
  understand: {
    path: "M9.81 15.9 9 18.75l-.81-2.85a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.85-.81a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.81 2.85a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.85.81a4.5 4.5 0 0 0-3.09 3.09z",
    detail: "M18 4.5l.45 1.35 1.35.45-1.35.45-.45 1.35-.45-1.35-1.35-.45 1.35-.45z",
    tone: "text-kin-gold-dark",
  },
  share: {
    path: "M7.22 10.91a2.25 2.25 0 1 0 0 2.18M7.22 10.91l9.56-5.31M7.22 13.09l9.56 5.31M19 7a2.25 2.25 0 1 0-2.22-1.4M19 17a2.25 2.25 0 1 0-2.22 1.4",
    detail: "",
    tone: "text-kin-lavender",
  },
};

/* ══════════════════════════════════════════════════════════════
   ICON TILE — clean single-stroke, tinted container
   Matches mobile screen style: colored bg, white icon
   ══════════════════════════════════════════════════════════════ */
function IconTile({ icon }) {
  return (
    <article className="kin-surface kin-surface-clouds rounded-2xl p-4">
      <div
        className="inline-flex h-14 w-14 items-center justify-center rounded-2xl"
        style={{ background: alpha(icon.color, "18"), border: borderAlpha(icon.color, "30") }}
      >
        <svg
          className="h-7 w-7"
          viewBox={icon.viewBox || "0 0 24 24"}
          fill="none"
          stroke={icon.color}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={icon.path} />
          {icon.extra && <path d={icon.extra} />}
        </svg>
      </div>

      <div className="mt-3">
        <p className="text-sm font-semibold text-kin-black">{icon.name}</p>
        <p className="mt-0.5 text-xs text-kin-muted">{icon.category}</p>
      </div>
    </article>
  );
}

/* ══════════════════════════════════════════════════════════════
   AI ACTION CARD
   ══════════════════════════════════════════════════════════════ */
function AIActionCard({ icon, label, detail }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-kin-lavender/20 bg-gradient-to-r from-kin-lavender/[0.06] to-transparent px-4 py-3">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
        style={{ background: alpha(icon.color, "18"), border: borderAlpha(icon.color, "30") }}
      >
        <svg
          className="h-[18px] w-[18px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke={icon.color}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={icon.path} />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-kin-black">{label}</p>
        <p className="text-[11px] text-kin-muted truncate">{detail}</p>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   REFERENCE BADGE
   ══════════════════════════════════════════════════════════════ */
function ReferenceBadge({ verified, source }) {
  const icon = verified
    ? aiIcons.find((i) => i.name === "Verified")
    : aiIcons.find((i) => i.name === "Unverified");
  const c = verified ? "var(--color-kin-green-soft)" : "var(--color-kin-gold-dark)";
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
      style={{ background: alpha(c, "0F"), border: borderAlpha(c, "30") }}
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={icon.path} />
      </svg>
      <span className="text-xs font-semibold" style={{ color: c }}>
        {source}
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SECTION
   ══════════════════════════════════════════════════════════════ */
function IconFamily({ label, icons }) {
  return (
    <>
      <p className="mt-10 text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
        {label}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {icons.map((icon) => (
          <IconTile key={icon.name} icon={icon} />
        ))}
      </div>
    </>
  );
}

export default function Iconography() {
  return (
    <section
      id="iconography"
      className="kin-section-glow border-t border-kin-olive/10 px-6 py-14 scroll-mt-16 sm:px-8 sm:py-16 gradient-wash"
    >
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
          06 - Iconography
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            Clean, single-stroke icons
          </h2>
          <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">
            One weight, one color. No halos, no dual layers. Each icon reads clearly from a 12px tab
            bar to a 48px dashboard card. The container tint carries the semantic meaning.
          </p>
        </div>

        <IconFamily label="Health &amp; Vitals" icons={healthIcons} />
        <IconFamily label="Data Sources" icons={dataIcons} />
        <IconFamily label="AI &amp; Intelligence" icons={aiIcons} />
        <IconFamily label="Platform &amp; UI" icons={platformIcons} />

        {/* AI Action Cards */}
        <p className="mt-10 text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
          AI Action Patterns
        </p>
        <p className="mt-2 mb-4 text-sm leading-relaxed text-kin-body/85">
          When the AI reads a document, looks something up, or processes data, these action cards
          show what it is doing in the conversation thread.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <AIActionCard
            icon={aiIcons[1]}
            label="Analysing your sleep data"
            detail="Comparing 30 nights of Apple Health data"
          />
          <AIActionCard
            icon={aiIcons[5]}
            label="Reading blood panel"
            detail="Lab report from Feb 12, 2026"
          />
          <AIActionCard
            icon={aiIcons[0]}
            label="Generating insight"
            detail="Cross-referencing sleep + HRV trends"
          />
          <AIActionCard
            icon={aiIcons[2]}
            label="Citing verified source"
            detail="Nature Medicine, 2024"
          />
        </div>

        {/* Reference Badges */}
        <p className="mt-10 text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
          Reference Badges
        </p>
        <p className="mt-2 mb-4 text-sm leading-relaxed text-kin-body/85">
          Sources cited by the AI are tagged as verified (peer-reviewed, institutional) or
          unverified (community, anecdotal) so users can assess trustworthiness at a glance.
        </p>
        <div className="flex flex-wrap gap-3">
          <ReferenceBadge verified source="Nature Medicine, 2024" />
          <ReferenceBadge verified source="WHO Guidelines" />
          <ReferenceBadge verified={false} source="Sleep Foundation Blog" />
          <ReferenceBadge verified={false} source="Community post" />
        </div>

        {/* Design Principles & In-Product UI */}
        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="kin-surface kin-surface-waves rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
              Icon Principles
            </p>
            <div className="mt-4 space-y-3">
              {principles.map((principle) => (
                <div
                  key={principle.title}
                  className="rounded-xl border border-kin-olive/12 bg-kin-white/80 px-3 py-3"
                >
                  <p className="text-sm font-semibold text-kin-black">{principle.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-kin-body/85">{principle.text}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="kin-surface kin-surface-splash rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
              In Product UI
            </p>

            <div className="mt-4 rounded-2xl border border-kin-olive/12 bg-kin-white/82 p-4">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
                Buttons + chips
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="small">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={healthIcons[1].path} />
                  </svg>
                  Add Lab Result
                </Button>
                <Badge>
                  <span className="h-1.5 w-1.5 rounded-full bg-kin-green" />
                  Stable
                </Badge>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-kin-olive/12 bg-kin-white/82 p-4">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
                Navigation + state
              </p>
              <div className="mt-3 space-y-2">
                {[
                  ["Vitals", healthIcons[0]],
                  ["Lab Panel", healthIcons[1]],
                  ["AI Insights", aiIcons[0]],
                ].map(([label, icon], index) => (
                  <div
                    key={label}
                    className={`flex items-center gap-2 rounded-xl px-3 py-2 ${
                      index === 0 ? "bg-kin-white border border-kin-olive/14" : "bg-kin-white/70"
                    }`}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={icon.color}
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={icon.path} />
                    </svg>
                    <span className="text-sm text-kin-body">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
